import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Award, ClipboardCheck, ClipboardList, Inbox, Lock, Pencil, SearchX, Unlock, UserX } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, Callout, ConfirmModal, EmptyState, FilePreview,
  LinkedText, PageHeader, StatsCard, StatsGrid, StatusBadge,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import AssignmentFacts from "./AssignmentFacts";
import SubmissionsTable from "./SubmissionsTable";
import {
  REVIEW_VIEWS, assignmentsBase, averageMarks, gradingOrder, marksCsv, reviewRows, viewCounts,
} from "./assignmentRules";

const LONG_INSTRUCTIONS = 600;

const PageSkeleton = () => (
  <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading assignment">
    <Shimmer height={150} />
    <StatsGrid cols={4}>{[0, 1, 2, 3].map((i) => <StatsCard key={i} loading />)}</StatsGrid>
    <Shimmer height={320} />
  </div>
);

const download = (text, fileName) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * AssignmentDetailPage — `/batch-assignments/:id` (and `/i/…`): one assignment,
 * what it asks for, and the queue of work to grade.
 *
 * Reading order: the assignment and its actions; four vitals (handed in · to
 * grade · graded · not yet); then every student of the batch, by the grader's
 * job. The view is in the URL (`?view=graded`), so coming back from grading
 * lands where the grader left.
 */
const AssignmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const base = assignmentsBase(getCurrentUser()?.usertype);

  const [state, setState] = useState({ loading: true, error: null, assignment: null, deadline: null, dateOnly: false });
  const [subs, setSubs] = useState({ loading: true, submissions: [], notSubmitted: [], rosterCount: 0 });
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [confirmClose, setConfirmClose] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const notify = useCallback((message, type = "success") => setToast({ show: true, message, type }), []);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    setSubs((s) => ({ ...s, loading: true }));
    apiClient.get(`/general-assignments/detail/${id}`)
      .then(({ data }) => setState({ loading: false, error: null, assignment: data.assignment, deadline: data.deadline, dateOnly: !!data.dateOnly }))
      .catch((error) => setState({ loading: false, error, assignment: null, deadline: null, dateOnly: false }));
    apiClient.get(`/general-assignments/${id}/submissions`)
      .then(({ data }) => setSubs({
        loading: false,
        submissions: data.submissions || [],
        notSubmitted: data.notSubmitted || [],
        rosterCount: data.rosterCount || 0,
      }))
      .catch(() => setSubs({ loading: false, submissions: [], notSubmitted: [], rosterCount: 0, failed: true }));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // A notice handed over by the form ("Assignment created") — shown once.
  useEffect(() => {
    if (!location.state?.notice) return;
    notify(location.state.notice);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location, navigate, notify]);

  const rows = useMemo(() => reviewRows(subs.submissions, subs.notSubmitted), [subs]);
  const counts = useMemo(() => viewCounts(rows), [rows]);

  const requestedView = searchParams.get("view");
  const view = REVIEW_VIEWS.includes(requestedView)
    ? requestedView
    : (subs.loading || counts.submitted > 0 ? "submitted" : "all");
  const setView = (next) => setSearchParams((prev) => {
    const params = new URLSearchParams(prev);
    params.set("view", next);
    return params;
  }, { replace: true });

  const openSubmission = useCallback(
    (submissionId) => navigate(`${base}/${id}/review/${submissionId}`, { state: { from: `${location.pathname}${location.search}` } }),
    [base, id, navigate, location.pathname, location.search],
  );

  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(base));
  const header = (title, description, actions) => (
    <PageHeader icon={ClipboardList} title={title} description={description} backButton onBack={goBack} actions={actions} />
  );

  if (state.loading && !state.assignment) {
    return <div className="tw-flex tw-flex-col tw-gap-4">{header("Assignment")}<PageSkeleton /></div>;
  }
  const a = state.assignment;
  if (!a) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header("Assignment")}
        {state.error?.response?.status === 404 ? (
          <EmptyState
            icon={SearchX}
            title="Assignment not found"
            description="It may have been deleted, or it belongs to a batch you don't teach."
            action={<AppButton variant="outline" onClick={() => navigate(base)}>Back to assignments</AppButton>}
          />
        ) : (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Try again</AppButton>}>
            {state.error?.response?.data?.message || "Could not load this assignment."}
          </Callout>
        )}
      </div>
    );
  }

  const closed = a.status === "closed";
  const average = averageMarks(rows);
  const handedIn = subs.submissions.length;

  const setStatus = async (next) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/general-assignments/${id}/${next === "closed" ? "close" : "reopen"}`);
      setState((s) => ({ ...s, assignment: { ...s.assignment, status: data.assignment?.status || next } }));
      notify(next === "closed" ? "Assignment closed. Students can no longer hand in." : "Assignment reopened.");
      setConfirmClose(false);
    } catch (err) {
      notify(err?.response?.data?.message || "Could not change the assignment's status.", "error");
    }
    setBusy(false);
  };

  const exportMarks = () => {
    const safeTitle = String(a.title || "assignment").replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "").slice(0, 60);
    download(marksCsv(rows, { maxMarks: a.maxMarks }), `marks-${safeTitle || "assignment"}.csv`);
  };

  const gradeNext = () => {
    const first = gradingOrder(rows.filter((r) => r.status === "submitted"))[0];
    if (first) openSubmission(first);
  };

  const instructions = a.description || "";
  const clamp = !showAll && instructions.length > LONG_INSTRUCTIONS;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {header(a.title, a.batchId?.name || "", (
        <>
          {closed ? (
            <AppButton size="sm" variant="outline" onClick={() => setStatus("published")} disabled={busy}>
              <Unlock size={16} /> Reopen
            </AppButton>
          ) : (
            <AppButton size="sm" variant="outline" onClick={() => setConfirmClose(true)} disabled={busy}>
              <Lock size={16} /> Close
            </AppButton>
          )}
          <AppButton size="sm" variant="outline" onClick={() => navigate(`${base}/edit/${id}`)}>
            <Pencil size={16} /> Edit
          </AppButton>
        </>
      ))}

      <AppCard>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <StatusBadge status={a.status} domain="assignment" />
            <AssignmentFacts
              deadline={state.deadline || a.dueDate}
              dateOnly={state.dateOnly}
              maxMarks={a.maxMarks}
              lateAllowed={a.allowLateSubmissions === true}
              closed={closed}
            />
          </div>
          {closed && (
            <Callout tone="info">This assignment is closed. Students can't hand in or resubmit until you reopen it.</Callout>
          )}
          {instructions ? (
            <div>
              <h2 className="tw-mb-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted">Instructions</h2>
              <div className={clamp ? "tw-max-h-40 tw-overflow-hidden [mask-image:linear-gradient(to_bottom,black_70%,transparent)]" : ""}>
                <LinkedText text={instructions} />
              </div>
              {instructions.length > LONG_INSTRUCTIONS && (
                <AppButton size="xs" variant="ghost" className="tw-mt-1" onClick={() => setShowAll((v) => !v)} aria-expanded={!clamp}>
                  {clamp ? "Show all" : "Show less"}
                </AppButton>
              )}
            </div>
          ) : (
            <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-subtle">No instructions were added.</p>
          )}
          {a.attachments?.length > 0 && (
            <div>
              <h2 className="tw-mb-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted">Reference files</h2>
              <FilePreview attachments={a.attachments} compact />
            </div>
          )}
        </AppCardContent>
      </AppCard>

      <StatsGrid cols={4}>
        <StatsCard
          title="Handed in"
          value={subs.loading ? "—" : handedIn}
          suffix={subs.rosterCount ? `of ${subs.rosterCount}` : undefined}
          icon={Inbox}
          iconColor="primary"
          loading={subs.loading}
        />
        <StatsCard title="To grade" value={counts.submitted} icon={ClipboardCheck} iconColor="warning" loading={subs.loading} />
        <StatsCard
          title="Graded"
          value={counts.graded}
          subtitle={average != null ? `Average ${average} / ${a.maxMarks}` : undefined}
          icon={Award}
          iconColor="success"
          loading={subs.loading}
        />
        <StatsCard title="Not submitted" value={counts.not_submitted} icon={UserX} iconColor="danger" loading={subs.loading} />
      </StatsGrid>

      <section aria-labelledby="ga-submissions" className="tw-flex tw-flex-col tw-gap-2">
        <h2 id="ga-submissions" className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">Submissions</h2>
        {subs.failed && (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Try again</AppButton>}>
            Could not load the submissions.
          </Callout>
        )}
        <SubmissionsTable
          rows={rows}
          maxMarks={a.maxMarks}
          view={view}
          onViewChange={setView}
          onOpen={openSubmission}
          onExport={exportMarks}
          onGradeNext={gradeNext}
          loading={subs.loading}
        />
      </section>

      <ConfirmModal
        show={confirmClose}
        title="Close this assignment?"
        message="Students won't be able to hand in or resubmit. Work already handed in stays, and you can still grade it. You can reopen it later."
        confirmText="Close assignment"
        variant="warning"
        loading={busy}
        onConfirm={() => setStatus("closed")}
        onHide={() => setConfirmClose(false)}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AssignmentDetailPage;
